import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Globe, 
  ShoppingCart, 
  Bot, 
  Calendar, 
  Database, 
  Store, 
  WholeWord as WordPress,
  Server,
  Mail,
  CheckCircle2,
  ArrowRight,
  Clock,
  AlertTriangle
} from 'lucide-react';
import SEO from '../components/SEO';

const packages = {
  websites: [
    {
      name: 'Basic',
      price: 'Frá 299.950 kr.',
      description: 'Fyrir minni fyrirtæki',
      features: [
        'Allt að 10 síður',
        'Responsive hönnun',
        'SEO uppsetning',
        'SSL vottun',
        'Tengingar við samfélagsmiðla',
        'Google Analytics',
        '3 mánuðir af viðhaldi'
      ],
      cta: 'Fá tilboð',
      link: '/fa-tilbod'
    },
    {
      name: 'Business',
      price: 'Frá 499.950 kr.',
      description: 'Fyrir meðalstór fyrirtæki',
      features: [
        'Allt að 20 síður',
        'Sérsniðin hönnun',
        'SEO bestun',
        'SSL vottun',
        'Samfélagsmiðlatengingar',
        'Google Analytics',
        'Google Business uppsetning',
        '6 mánuðir af viðhaldi',
        'Innri leitarvél',
        'Fréttakerfi'
      ],
      popular: true,
      cta: 'Fá tilboð',
      link: '/fa-tilbod'
    },
    {
      name: 'Enterprise',
      price: 'Tilboð',
      description: 'Fyrir stærri fyrirtæki',
      features: [
        'Ótakmarkaðar síður',
        'Sérsniðin hönnun',
        'Ítarleg SEO bestun',
        'SSL vottun',
        'API tengingar',
        'Sérsniðin virkni',
        'Öryggisúttekt',
        '12 mánuðir af viðhaldi',
        'Forgangsþjónusta',
        'Sérsniðið stjórnborð'
      ],
      cta: 'Fá tilboð',
      link: '/fa-tilbod'
    }
  ],
  solutions: [
    {
      icon: WordPress,
      name: 'WordPress Express',
      price: '99.950 kr.',
      description: 'Hraðvirk WordPress lausn',
      features: [
        'Staðlað WordPress þema',
        'Grunnstilling og uppsetning',
        'Tengingar við samfélagsmiðla',
        'SSL vottun',
        '3 síður innifaldar'
      ],
      cta: 'Panta núna',
      link: '/lausnir/wordpress-express/panta'
    },
    {
      icon: Store,
      name: 'WooCommerce Express',
      price: '149.950 kr.',
      description: 'Tilbúin netverslunarlausn',
      features: [
        'Staðlað WooCommerce þema',
        'Greiðslugáttir',
        'Vörustjórnun',
        'SSL vottun',
        '5 síður innifaldar'
      ],
      cta: 'Panta núna',
      link: '/lausnir/wordpress-express/panta'
    },
    {
      icon: Server,
      name: 'Vefhýsing',
      price: 'Frá 4.990 kr./mán',
      description: 'Örugg hýsing fyrir vefsíður',
      features: [
        'Diskpláss og umferð',
        'SSL vottun',
        'Dagleg öryggisafritun',
        'DNS stjórnun',
        '24/7 vöktun'
      ],
      cta: 'Skoða pakka',
      link: '/lausnir/vefhysing'
    }
  ]
};

const Pricing = () => {
  return (
    <>
      <SEO 
        title="Verðskrá"
        description="Skoðaðu verðskrána okkar. Við bjóðum upp á hagkvæmar lausnir sem henta öllum stærðum fyrirtækja."
        keywords="verð, verðskrá, pakkar, kostnaður, fjárfesting"
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hourly Rates Section */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <motion.div 
              className="text-center max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Tímagjald</h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <motion.div 
                className="bg-gray-50 rounded-xl p-8 border border-gray-100"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Almenn þjónusta</h3>
                <div className="text-4xl font-bold text-[#EA295A] text-center mb-2">14.900 kr.</div>
                <p className="text-gray-500 text-center text-sm">án VSK á klst.</p>
              </motion.div>

              <motion.div 
                className="bg-gray-50 rounded-xl p-8 border border-gray-100"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Neyðarþjónusta</h3>
                <div className="text-4xl font-bold text-[#EA295A] text-center mb-2">22.350 kr.</div>
                <p className="text-gray-500 text-center text-sm">án VSK á klst.</p>
                <p className="text-gray-500 text-center text-xs mt-2">(50% álag utan opnunartíma)</p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Verðskrá
            </h1>
            <p className="text-xl text-gray-600">
              Veldu þann pakka sem hentar þínum þörfum best. Öll verð eru með VSK nema annað sé tekið fram.
            </p>
          </motion.div>

          {/* Website Packages */}
          <motion.div 
            className="py-24 bg-white rounded-2xl shadow-sm mb-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="px-8">
              <motion.div 
                className="text-center max-w-3xl mx-auto mb-16"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Vefsíðugerð
                </h2>
                <p className="text-xl text-gray-600">
                  Sérsniðnar veflausnir fyrir öll fyrirtæki
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packages.websites.map((pkg, index) => (
                  <motion.div
                    key={index}
                    className={`bg-gray-50 rounded-xl p-8 border ${
                      pkg.popular ? 'border-[#EA295A]' : 'border-gray-100'
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    {pkg.popular && (
                      <div className="bg-[#EA295A]/10 text-[#EA295A] text-sm font-medium px-3 py-1 rounded-full inline-block mb-4">
                        Vinsælast
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <div className="text-3xl font-bold text-[#EA295A] mb-4">{pkg.price}</div>
                    <p className="text-gray-600 mb-6">{pkg.description}</p>
                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-gray-600">
                          <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={pkg.link}>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center ${
                          pkg.popular
                            ? 'bg-[#EA295A] text-white hover:bg-[#d01d4c]'
                            : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                        } transition-all`}
                      >
                        {pkg.cta}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </motion.button>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Express Solutions */}
          <motion.div 
            className="py-24 bg-white rounded-2xl shadow-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="px-8">
              <motion.div 
                className="text-center max-w-3xl mx-auto mb-16"
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Express lausnir
                </h2>
                <p className="text-xl text-gray-600">
                  Hagkvæmar og hraðvirkar lausnir
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {packages.solutions.map((solution, index) => (
                  <motion.div
                    key={index}
                    className="bg-gray-50 rounded-xl p-8 border border-gray-100"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                      <solution.icon className="w-6 h-6 text-[#EA295A]" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{solution.name}</h3>
                    <div className="text-3xl font-bold text-[#EA295A] mb-4">{solution.price}</div>
                    <p className="text-gray-600 mb-6">{solution.description}</p>
                    <ul className="space-y-3 mb-8">
                      {solution.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-gray-600">
                          <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to={solution.link}>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-[#EA295A] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#d01d4c] transition-all flex items-center justify-center"
                      >
                        {solution.cta}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </motion.button>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div 
            className="py-24"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
              <motion.h2 
                className="text-3xl font-bold text-white mb-6"
              >
                Ertu með sérstök verkefni í huga?
              </motion.h2>
              <motion.p 
                className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
              >
                Við bjóðum upp á sérsniðnar lausnir fyrir stærri verkefni. 
                Hafðu samband og við finnum bestu lausnina fyrir þig.
              </motion.p>
              <motion.div>
                <Link to="/hafa-samband">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                  >
                    Hafa samband
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Pricing;